export type EmbedMessageType =
  | 'ready'
  | 'documentGenerated'
  | 'paymentComplete'
  | 'error'
  | 'resize'
  | 'navigationChange'
  | 'documentSavedToParent'
  | 'saveDocumentRequest'
  | 'documentUploadRequest'
  | 'documentUploadResponse'
  | 'documentUploadComplete'
  | 'documentStoredLocally'
  | 'embedClosed';

export interface EmbedMessage {
  type: EmbedMessageType;
  source: 'tenancy-agreement-embed';
  timestamp: number;
  data?: any;
}

export interface DocumentGeneratedMessage extends EmbedMessage {
  type: 'documentGenerated';
  data: {
    documentType: string;
    documentId: string | null;
    tier: 'standard' | 'enhanced' | 'premium';
  };
}

export interface PaymentCompleteMessage extends EmbedMessage {
  type: 'paymentComplete';
  data: {
    tier: 'enhanced' | 'premium';
    documentId: string;
  };
}

export interface ErrorMessage extends EmbedMessage {
  type: 'error';
  data: {
    message: string;
    code?: string;
  };
}

export interface ResizeMessage extends EmbedMessage {
  type: 'resize';
  data: {
    height: number;
    width: number;
  };
}

export interface NavigationChangeMessage extends EmbedMessage {
  type: 'navigationChange';
  data: {
    view: 'landing' | 'generate' | 'review' | 'confirmation';
  };
}

export interface SaveDocumentRequestMessage extends EmbedMessage {
  type: 'saveDocumentRequest';
  data: {
    documentGenerationId: string;
    documentType: string;
    propertyId?: string;
    standardContent: string;
    enhancedContent?: string;
    premiumContent?: string;
    formData: Record<string, any>;
    subscriptionTier: string;
    unlockedTiers: string[];
    timestamp: number;
  };
}

export interface DocumentSavedToParentMessage extends EmbedMessage {
  type: 'documentSavedToParent';
  data: {
    success: boolean;
    parentDocumentId?: string;
    error?: string;
  };
}

export function createEmbedMessage(
  type: EmbedMessageType,
  data?: any
): EmbedMessage {
  return {
    type,
    source: 'tenancy-agreement-embed',
    timestamp: Date.now(),
    data,
  };
}

export interface DocumentUploadRequestMessage extends EmbedMessage {
  type: 'documentUploadRequest';
  data: {
    requestId: string;
    documentId: string;
    documentType: string;
    documentTitle: string;
    pdfUrl: string;
    metadata: {
      propertyAddress?: string;
      generatedAt: string;
      tier: string;
      formData: Record<string, any>;
    };
  };
}

export interface DocumentUploadResponseMessage extends EmbedMessage {
  type: 'documentUploadResponse';
  data: {
    requestId: string;
    success: boolean;
    parentDocumentId?: string;
    error?: string;
  };
}

export interface DocumentUploadCompleteMessage extends EmbedMessage {
  type: 'documentUploadComplete';
  data: {
    documentId: string;
    parentDocumentId: string;
    documentType: string;
  };
}

export interface DocumentStoredLocallyMessage extends EmbedMessage {
  type: 'documentStoredLocally';
  data: {
    documentId: string;
    reason: string;
  };
}

export interface EmbedClosedMessage extends EmbedMessage {
  type: 'embedClosed';
  data: {
    documentId?: string;
    exitMethod: string;
    timeOnPage?: number;
  };
}

export function sendMessageToParent(message: Partial<EmbedMessage>): void {
  if (window.parent && window.parent !== window) {
    const fullMessage = createEmbedMessage(message.type!, message.data);
    window.parent.postMessage(fullMessage, '*');
  }
}

export function isEmbedMessage(message: any): message is EmbedMessage {
  return (
    message &&
    typeof message === 'object' &&
    message.source === 'tenancy-agreement-embed' &&
    typeof message.type === 'string' &&
    typeof message.timestamp === 'number'
  );
}
